import {CheckIcon, CloudIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {ReactNode, SyntheticEvent, useState} from "react";

export type OtsStatusStripItem = {
  id?: string;
  label: string;
  /** Custom interactive chip, such as a tray trigger. Owns its accessible name. */
  content?: ReactNode;
  tooltip?: string;
  icon?: ReactNode;
  /** `warning` fills the chip in amber, for a state everyone on the page should notice; `danger` in red, for one that needs acting on. */
  tone?: "default" | "warning" | "danger";
  onClick?: () => void;
};

export type OtsStatusStripProps = {
  /** Something is still loading; the lead chip pulses. */
  loading?: boolean;
  /** Text of the lead chip; defaults to Loading or Ready. */
  message?: string;
  loadingTooltip?: string;
  items?: OtsStatusStripItem[];
  /** Destinations the person pinned, kept at the strip's far end; each has its own way to unpin. */
  pinned?: OtsStatusStripPin[];
  className?: string;
};

export type OtsStatusStripPin = {
  id: string;
  label: string;
  icon?: ReactNode;
  /** The page the person is on; drawn in the brand color. */
  current?: boolean;
  onClick: () => void;
  onUnpin: () => void;
};

const LoadingStatusIcon = ({loading}: {loading?: boolean}) => (
  <span className={["relative grid size-4 shrink-0 place-items-center", loading ? "animate-pulse" : ""].join(" ")}>
    {loading && <span className="absolute inset-0 rounded-full bg-[#E1251B]/20 blur-[3px]"/>}
    <CloudIcon
      aria-hidden="true"
      className={["relative size-4 transition", loading ? "text-[#E1251B]/75" : "text-zinc-400 dark:text-zinc-300"].join(" ")}/>
    {!loading && (
      <span className="absolute -bottom-0.5 -right-0.5 grid size-2.5 place-items-center rounded-full bg-white text-emerald-600 shadow-sm ring-1 ring-zinc-950/10 dark:bg-[#081222] dark:text-emerald-300 dark:ring-white/15">
        <CheckIcon aria-hidden="true" className="size-2"/>
      </span>
    )}
  </span>
);

export const getOtsStatusChipClassName = (tone: OtsStatusStripItem["tone"] = "default", interactive = true) => [
    "inline-flex h-5 max-w-72 items-center gap-1.5 rounded-full border px-2 text-[0.7rem]/5 font-medium shadow-sm transition",
    tone === "warning"
      ? "border-amber-600/30 bg-amber-400 font-bold text-amber-950"
      : tone === "danger"
        ? "border-red-700/30 bg-[#E1251B] font-bold text-white"
        : "border-[color-mix(in_srgb,var(--ots-text,#09090b)_12%,transparent)] bg-[color-mix(in_srgb,var(--ots-text,#09090b)_4%,transparent)] text-[color-mix(in_srgb,var(--ots-text,#09090b)_80%,transparent)]",
    interactive
      ? tone === "warning" ? "hover:bg-amber-300" : tone === "danger" ? "hover:bg-[#c81f16]" : "hover:bg-[color-mix(in_srgb,var(--ots-text,#09090b)_8%,transparent)]"
      : "",
  ].join(" ");


const StatusItem = ({item, icon, pulse}: {item: OtsStatusStripItem; icon?: ReactNode; pulse?: boolean}) => {
  const [tip, setTip] = useState<{left: number; top: number}>();
  const showTip = (event: SyntheticEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTip({left: Math.round(rect.left), top: Math.round(rect.bottom + 4)});
  };
  const hideTip = () => setTip(undefined);
  const className = getOtsStatusChipClassName(item.tone, !!item.onClick);
  const content = <>
    {icon ?? (item.icon != null
      ? <span aria-hidden="true" className="grid size-3.5 shrink-0 place-items-center [&>svg]:size-3.5">{item.icon}</span>
      : <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-[#E1251B] shadow-[0_0_0_3px_rgba(225,37,27,0.12)]"/>)}
    <span className={["truncate", pulse ? "animate-pulse" : ""].join(" ")}>{item.label}</span>
  </>;

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={item.tooltip ? showTip : undefined}
      onMouseLeave={item.tooltip ? hideTip : undefined}
      onFocus={item.tooltip ? showTip : undefined}
      onBlur={item.tooltip ? hideTip : undefined}>
      {item.onClick
        ? <button type="button" onClick={item.onClick} className={className} data-status-id={item.id}>{content}</button>
        : <span className={className} data-status-id={item.id} tabIndex={item.tooltip ? 0 : undefined}>{content}</span>}
      {/* Fixed, below the chip: the strip scrolls sideways, which would clip a tooltip placed inside it. */}
      {item.tooltip && tip && (
        <span
          role="tooltip"
          style={{position: "fixed", left: tip.left, top: tip.top}}
          className="pointer-events-none z-[1002] max-w-80 whitespace-nowrap rounded-md bg-zinc-950 px-2 py-1 text-xs/5 text-white shadow-lg dark:bg-white dark:text-zinc-950">
          {item.tooltip}
        </span>
      )}
    </span>
  );
};

const PinnedItem = ({pin}: {pin: OtsStatusStripPin}) => (
  <span
    data-pin-id={pin.id}
    className={[
      "group/chip inline-flex h-5 max-w-56 shrink-0 items-center rounded-full border text-[0.7rem]/5 font-medium shadow-sm",
      pin.current
        ? "border-[color-mix(in_srgb,var(--ots-primary,#2563eb)_45%,transparent)] bg-[color-mix(in_srgb,var(--ots-primary,#2563eb)_12%,transparent)] text-[var(--ots-primary,#2563eb)]"
        : "border-[color-mix(in_srgb,var(--ots-text,#09090b)_12%,transparent)] bg-[color-mix(in_srgb,var(--ots-text,#09090b)_4%,transparent)] text-[color-mix(in_srgb,var(--ots-text,#09090b)_80%,transparent)]",
    ].join(" ")}>
    <button
      type="button"
      onClick={pin.onClick}
      aria-current={pin.current ? "page" : undefined}
      data-status-id={pin.id}
      className="inline-flex h-full min-w-0 items-center gap-1.5 rounded-l-full pl-2 pr-1 hover:underline">
      {pin.icon != null && <span aria-hidden="true" className="grid size-3.5 shrink-0 place-items-center [&>svg]:size-3.5">{pin.icon}</span>}
      <span className="truncate">{pin.label}</span>
    </button>
    <button
      type="button"
      onClick={pin.onUnpin}
      aria-label={`Unpin ${pin.label}`}
      title={`Unpin ${pin.label}`}
      data-status-id={`${pin.id}:unpin`}
      className="mr-0.5 grid size-4 shrink-0 place-items-center rounded-full opacity-50 transition hover:bg-[color-mix(in_srgb,var(--ots-text,#09090b)_10%,transparent)] hover:opacity-100 focus-visible:opacity-100 group-hover/chip:opacity-80">
      <XMarkIcon aria-hidden="true" className="size-3"/>
    </button>
  </span>
);

/**
 * The thin strip under an application's navbar: whether the app is ready, then a chip for each thing worth
 * knowing right now, and at its far end the destinations the person pinned. The same strip the dashboard has, for
 * products that share its top navigation.
 */
export const OtsStatusStrip = ({loading, message, loadingTooltip, items = [], pinned = [], className}: OtsStatusStripProps) => (
  <div
    role="status"
    aria-label="Status"
    className={[
      "flex min-h-6 items-center gap-2 overflow-x-auto border-t border-[var(--ots-outline-variant,rgba(9,9,11,0.05))] bg-[var(--ots-surface,#fff)] px-3 py-1 text-xs/5 text-[var(--ots-text-muted,#52525b)] dark:border-[var(--ots-outline-variant,rgba(255,255,255,0.1))] dark:bg-[var(--ots-surface,#081222)] dark:text-[var(--ots-text-muted,#e4e4e7)]",
      className,
    ].filter(Boolean).join(" ")}>
    <StatusItem
      item={{label: message || loadingTooltip || (loading ? "Loading" : "Ready"), tooltip: loadingTooltip}}
      icon={<LoadingStatusIcon loading={loading}/>}
      pulse={loading}/>
    {items.map(item => item.content != null
      ? <span key={item.id ?? item.label} className="inline-flex shrink-0" data-status-id={item.id}>{item.content}</span>
      : <StatusItem key={item.id ?? item.label} item={item}/>)}
    {pinned.length > 0 && (
      <span role="group" aria-label="Pinned" className="ml-auto flex shrink-0 items-center gap-1.5">
        {pinned.map(pin => <PinnedItem key={pin.id} pin={pin}/>)}
      </span>
    )}
  </div>
);
